import { ConfigConstants } from '../../config/config-constants';
import { NOTIFICATION_STATUS_CODE } from '../../enums/notification-status-code.enum';
import { getUuidV4 } from '../../utilities/generic-utilities';
// Type-only, and the file rather than the barrel: the address slice's own state file imports
// `AddressModel`, so a value import here would make the notification conversion part of that cycle.
import type { AddressState } from '../address/address-collection.state';
import type { ODataNotification, ODataNotificationAddress } from '../odata-types/notification.type';
import { NotificationDataFields } from './notification-collection.state';
import {
    odataToNotificationCode,
    odataToNotificationContractRow,
    odataToNotificationDropOffPoint,
    odataToNotificationEmail,
    odataToNotificationEquipment,
    odataToNotificationHistoryEntry,
    odataToNotificationLongTextLines,
    odataToNotificationPaperworkQuestion,
    odataToNotificationPart,
    odataToNotificationParticipant,
    odataToNotificationTask,
    parseIntegerField,
} from './odata-to-notification-entities';

const ADDR_KEY_MULTIPLIER = 10000000;

/**
 * One converted notification: the notification the store holds, plus the addresses taken out of it.
 * The notification refers to them by id only ({@link NotificationDataFields.customerAddressId},
 * {@link NotificationDataFields.addressIds}) — the address slice is the one place addresses live,
 * so the caller has to fold these into it. `NotificationEffects.mergeAddresses$` does that for a
 * load, and rewrites the links to whichever address survived its de-duplication.
 */
export type ConvertedNotification = Readonly<{
    notification: NotificationDataFields;
    /** The notification's own customer address, the flat `Name1`/`Street`/… header fields. */
    customerAddress: AddressState;
    /** The addresses from the payload's `AddressSet`, in the order the backend sent them. */
    addressSet: AddressState[];
}>;

/**
 * The notification's own customer address — the header's flat `Name1`/`Street`/… fields — as an
 * address the address slice can hold. `email` and `phone` both carry `TelNumber`, which despite
 * its name is the customer's e-mail address.
 *
 * @param odata the notification payload
 */
function customerAddressToState(odata: ODataNotification): AddressState {
    return {
        id: getUuidV4(),
        addrKey: Math.floor(Math.random() * ADDR_KEY_MULTIPLIER).toString(),
        addrNumber: odata.AddrNumber,
        addrText: '',
        allowToHide: false,
        building: odata.Building,
        city: odata.City,
        contactName1: odata.ContactName1,
        contactName2: odata.ContactName1,
        contactPhone: odata.ContactTel,
        contactType: odata.ContactType,
        country: odata.Country ?? '',
        deleted: false,
        department: odata.Department,
        email: odata.TelNumber,
        fax1: odata.FaxNumber1,
        fax2: odata.FaxNumber2,
        fax3: odata.FaxNumber3 ?? '',
        fax4: odata.FaxNumber4 ?? '',
        floor: odata.Floor,
        isFromAddressSet: false,
        name1: odata.Name1Long,
        name2: odata.Name2Long,
        name3: odata.Name3Long,
        name4: odata.Name4Long,
        name1shortP58: odata.Name1,
        name2shortP58: odata.Name2,
        // freshly built from the payload that just arrived, so it is in scope by construction —
        // `NotificationEffects.mergeAddresses` narrows the collection to this scope afterwards.
        outOfScope: false,
        phone: odata.TelNumber,
        postCode: odata.PostCode,
        room: odata.Room,
        state: '',
        stateOfUSA: undefined,
        street: odata.Street,
        visible: true,
    };
}

/**
 * One `AddressSet` entry as an address the address slice can hold. The short form carries no names
 * and no contact, and is the one the user may hide (`allowToHide`).
 *
 * @param address the `AddressSet` row
 */
function shortAddressToState(address: ODataNotificationAddress): AddressState {
    return {
        id: getUuidV4(),
        addrKey: Math.floor(Math.random() * ADDR_KEY_MULTIPLIER).toString(),
        addrNumber: address.AddrNumber,
        addrText: address.AddrNotes,
        allowToHide: true,
        building: '',
        city: address.City,
        contactName1: '',
        contactName2: '',
        contactPhone: '',
        contactType: '',
        country: address.Country ?? '',
        deleted: false,
        department: '',
        email: '',
        fax1: '',
        fax2: '',
        fax3: '',
        fax4: '',
        floor: '',
        isFromAddressSet: true,
        name1: '',
        name2: '',
        name3: '',
        name4: '',
        name1shortP58: '',
        name2shortP58: '',
        // freshly built from the payload that just arrived, so it is in scope by construction —
        // `NotificationEffects.mergeAddresses` narrows the collection to this scope afterwards.
        outOfScope: false,
        phone: '',
        postCode: address.PostalCode,
        room: '',
        state: '',
        stateOfUSA: undefined,
        street: address.Street,
        visible: true,
    };
}

/**
 * The in-vivo/in-vitro process indicator, `UNKNOWN` for anything else the backend sends.
 *
 * @param invitroInd the raw `InvitroInd` field
 */
function processIndicatorOf(invitroInd: string): ConfigConstants.PROCESS_INDICATOR {
    return invitroInd === ConfigConstants.PROCESS_INDICATOR.INVIVO ||
        invitroInd === ConfigConstants.PROCESS_INDICATOR.INVITRO
        ? invitroInd
        : ConfigConstants.PROCESS_INDICATOR.UNKNOWN;
}

/**
 * Turns one `NdpHeaderSet` payload into the notification the store holds: the numbers parsed, the
 * expanded entity sets and long texts unwrapped, the identifier the payload repeats on every row
 * reduced to one {@link NotificationDataFields.notificationId}, and the addresses split off (see
 * {@link ConvertedNotification}).
 *
 * Pure and total: no config, no clock, no logger, no injector. Everything whose value would depend
 * on one of those is derived instead — see the doc comment on `NotificationState` for what, and
 * `notification-longtext.converters.ts` for the converters that do it.
 *
 * @param odata the payload exactly as `NdpHeaderSet` sent it
 */
export function odataToNotificationState(odata: ODataNotification): ConvertedNotification {
    const equipments = (odata.EquipmentsSet?.results ?? []).map(odataToNotificationEquipment);
    const customerAddress = customerAddressToState(odata);
    const addressSet = (odata.AddressSet?.results ?? []).map(shortAddressToState);

    const notification: NotificationDataFields = {
        notificationId: { frontId: odata.FrontId, qmNum: odata.NotificationNo, maNum: odata.TaskNumber },

        functionalLocation: odata.FunctionalLoc,
        messageType: odata.MessagetypeCSE,
        companyCode: odata.CompanyCode,
        notificationType: odata.NotificationType,
        processIndicator: processIndicatorOf(odata.InvitroInd),
        userStatus: odata.UserStatus,
        taskGroup: odata.TaskGroup,
        mqQmnum: odata.Notification,
        orderType: odata.SalesDocType,
        activityNumber: parseIntegerField(odata.ActivityNo),
        salesOrg: odata.SalesOrg ?? '',
        languageId: odata.LanguageId ? odata.LanguageId : 'EN',
        languageText: odata.LanguageText,
        deliveryType: odata.DelivType,
        effect: odata.Effect,
        anlInaktCode: odata.AnlInaktCode,
        assigned: odata.Assigned,
        dispatcher: odata.Dispatcher,
        ndpVersion: parseIntegerField(odata.NdpVersion),
        preclarified: parseIntegerField(odata.PreClarified),
        repairLimit: parseIntegerField(odata.RepairLimit),
        // TODO: Check if it is a valid case to receive "" here
        backendStatusCode: (odata.Status || NOTIFICATION_STATUS_CODE.DISP) as NOTIFICATION_STATUS_CODE,

        plannedStartDate: odata.PlanStartDate,
        plannedStartTime: odata.PlanStartTime,
        plannedEndDate: odata.PlanEndDate,
        plannedEndTime: odata.PlanEndTime,
        startMalfunctionDate: odata.StartMalfunctionDate,
        startMalfunctionTime: odata.StartMalfunctionTime,
        timeZone: { utcDiff: odata.UTCDiff, utcSign: odata.UTCSign, name: odata.TimeZone },

        customerAddressId: customerAddress.id,
        addressIds: addressSet.map((address) => address.id),
        phoneRemote: odata.TelRemote,
        customerPurchaseOrderNumber: odata.CustPONo,
        notificationText: odata.NotificationText,
        clarifyText: odata.ClarifyText,
        clarifyText1: odata.ClarifyText1,

        notificationLines: odataToNotificationLongTextLines(odata.NotifLtextSet?.results),
        objectInformationLines: odataToNotificationLongTextLines(odata.ObjInfLtextSet?.results),
        preclarificationLines: odataToNotificationLongTextLines(odata.PreClLtextSet?.results),
        historyLines: odataToNotificationLongTextLines(odata.HistoryLtextSet?.results),
        hotsiteLines: odataToNotificationLongTextLines(odata.HotsiteLtextSet?.results),
        internalInfoLines: odataToNotificationLongTextLines(odata.IntInfLtextSet?.results),
        issueReportLines: odataToNotificationLongTextLines(odata.IssueRepLtextSet?.results),
        rentedMaterialLines: odataToNotificationLongTextLines(odata.RentLtextSet?.results),
        taxiLines: odataToNotificationLongTextLines(odata.TaxiLtextSet?.results),
        sourceReplacementLines: odataToNotificationLongTextLines(odata.SorcLTextSet?.results),

        codes: (odata.CodeSet?.results ?? []).map(odataToNotificationCode),
        contracts: (odata.ContractSet?.results ?? []).map(odataToNotificationContractRow),
        dropOffPoints: (odata.DopSet?.results ?? []).map(odataToNotificationDropOffPoint),
        emails: (odata.EmailSet?.results ?? []).map(odataToNotificationEmail),
        equipments,
        history: (odata.HistorySet?.results ?? []).map(odataToNotificationHistoryEntry),
        parts: (odata.PartSet?.results ?? []).map(odataToNotificationPart),
        participants: (odata.ParticipantSet?.results ?? []).map(odataToNotificationParticipant),
        tasks: (odata.TaskSet?.results ?? []).map(odataToNotificationTask),
        paperwork: (odata.PaperWorkSet?.results ?? []).map(odataToNotificationPaperworkQuestion),
        equipmentCount: odata.EquipmentCount ?? equipments.length,
        formsCount: odata.FormsCount,
    };

    return { notification, customerAddress, addressSet };
}
